import { Button, Typography, theme } from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";
import { useGuard } from "../Guard";

export interface LiveNotificationToastsProps {
  readonly onOpen?: () => void;
}

interface Toast {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
}

const DISMISS_MS = 5000;
const MAX_VISIBLE = 3;

const LiveNotificationToasts = memo(function LiveNotificationToasts(
  props: LiveNotificationToastsProps,
) {
  const { token } = theme.useToken();
  const { on, off } = useGuard();
  const { onOpen } = props;
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((uid: string) => {
    setToasts((current) => current.filter((t) => t.uid !== uid));
    const timer = timers.current.get(uid);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(uid);
    }
  }, []);

  useEffect(() => {
    const handler = (
      recipientUid: string,
      _notificationUid: string,
      title: string,
      message: string,
    ) => {
      setToasts((current) => {
        const next = [
          { uid: recipientUid, title, message },
          ...current.filter((t) => t.uid !== recipientUid),
        ];
        next.slice(MAX_VISIBLE).forEach((t) => {
          const timer = timers.current.get(t.uid);
          if (timer) {
            clearTimeout(timer);
            timers.current.delete(t.uid);
          }
        });
        return next.slice(0, MAX_VISIBLE);
      });

      const existing = timers.current.get(recipientUid);
      if (existing) clearTimeout(existing);
      timers.current.set(
        recipientUid,
        setTimeout(() => remove(recipientUid), DISMISS_MS),
      );
    };

    on("ReceiveNotification", handler);
    return () => off("ReceiveNotification", handler);
  }, [on, off, remove]);

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  if (!toasts.length) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style={{ pointerEvents: "none" }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.uid}
          className="animate__animated animate__fadeInRight flex w-[440px] max-w-[90vw] cursor-pointer flex-row items-start gap-2 p-4"
          style={{
            pointerEvents: "auto",
            backgroundColor: token.colorBgElevated,
            borderRadius: token.borderRadius,
            boxShadow: token.boxShadowSecondary,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
          onClick={() => {
            onOpen?.();
            remove(toast.uid);
          }}
        >
          <div className="flex flex-auto flex-col overflow-hidden">
            <Typography.Text strong ellipsis={{ tooltip: toast.title }}>
              {toast.title}
            </Typography.Text>
            <Typography.Paragraph
              type="secondary"
              className="mb-0 mt-1"
              ellipsis={{ rows: 4, tooltip: toast.message }}
            >
              {toast.message}
            </Typography.Paragraph>
          </div>
          <Button
            type="text"
            size="small"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              remove(toast.uid);
            }}
            icon={<Icon className="text-lg" component={Icons.CloseIcon} />}
          />
        </div>
      ))}
    </div>
  );
});

export default LiveNotificationToasts;
